import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { asistenteApi, asistenteKeys } from '../asistente.api';
import type { AlertasRespuesta } from '../asistente.types';

/**
 * Estado del asistente y consultas sugeridas.
 *
 * Se cachean 5 minutos: son datos de configuración que cambian muy poco.
 */
export function useAsistenteStatus() {
  return useQuery({
    queryKey: asistenteKeys.status,
    queryFn: ({ signal }) => asistenteApi.status(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useAsistenteSugerencias() {
  return useQuery({
    queryKey: asistenteKeys.sugerencias,
    queryFn: ({ signal }) => asistenteApi.sugerencias(signal),
    staleTime: 30 * 60_000,
    retry: 1,
  });
}

/**
 * Avisos del momento (ventas bajas, stock agotado, documentos vencidos…).
 * Se refrescan cada 10 minutos: son datos operativos que sí cambian.
 */
export function useAsistenteAlertas(habilitado = true) {
  return useQuery({
    queryKey: asistenteKeys.alertas,
    queryFn: ({ signal }) => asistenteApi.alertas(signal),
    staleTime: 10 * 60_000,
    enabled: habilitado,
    retry: false,
  });
}

/**
 * Descarta un aviso (y lo quita de la pantalla al instante).
 *
 * Se actualiza la caché de forma optimista porque el usuario acaba de pulsar
 * «cerrar»: esperar a la respuesta del servidor daría sensación de lentitud. Si
 * la petición falla, se recupera la lista original.
 */
export function useDescartarAlerta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tipo: string) => asistenteApi.descartarAlerta(tipo),
    onMutate: async (tipo) => {
      await queryClient.cancelQueries({ queryKey: asistenteKeys.alertas });
      const previo = queryClient.getQueryData<AlertasRespuesta>(asistenteKeys.alertas);

      if (previo) {
        queryClient.setQueryData<AlertasRespuesta>(asistenteKeys.alertas, {
          alertas: previo.alertas.filter((alerta) => alerta.tipo !== tipo),
          descartadas: previo.descartadas + 1,
        });
      }

      return { previo };
    },
    onError: (_error, _tipo, contexto) => {
      if (contexto?.previo) queryClient.setQueryData(asistenteKeys.alertas, contexto.previo);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: asistenteKeys.alertas });
    },
  });
}

/** Vuelve a mostrar los avisos que el usuario había descartado. */
export function useRestaurarAlertas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => asistenteApi.restaurarAlertas(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: asistenteKeys.alertas });
    },
  });
}

/** Consumo de IA del usuario (tokens y consultas del mes). */
export function useAsistenteConsumo() {
  return useQuery({
    queryKey: asistenteKeys.consumo,
    queryFn: ({ signal }) => asistenteApi.consumo(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
